import MapLibreGlDirections from "@maplibre/maplibre-gl-directions";
import { type SEOHandle } from "@nasa-gcn/remix-seo";
import {
  json,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
} from "@remix-run/node";
import { type MetaFunction, Outlet, useLoaderData } from "@remix-run/react";
import { useEffect, useState } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
// @ts-ignore
import Mapa from "#app/components/mapaplanejador.jsx";
import { Skeleton } from "#app/components/ui/skeleton.tsx";
import { getUserId } from "#app/utils/auth.server.js";
import { prisma } from "#app/utils/db.server";

export const meta: MetaFunction = ({ location }) => {
  const searchQuery = new URLSearchParams(location.search);

  let state = searchQuery.get("estado");
  let city = searchQuery.get("cidade");

  //clean maker and capitalize first letters
  if (city) {
    city = city.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //clean state and capitalize first letters
  if (state) {
    state = state.replace(/-/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  //build url
  const currentUrl = `https://carregados.com.br/mapa`;

  let title = "Mapa de Estações de Carregamento de Carros Elétricos";
  let description =
    "Encontre estações de carregamento de carros elétricos próximas a você. O mapa de estações de carregamento mais completo do Brasil.";

  return [
    { title: title },
    {
      name: "description",
      content: description,
    },
    {
      itemProp: "name",
      content: title,
    },
    {
      itemProp: "description",
      content: description,
    },
    { itemProp: "image", content: "https://carregados.com.br/img/og-main.jpg" },
    { property: "og:url", content: currentUrl },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://carregados.com.br/img/og-main.jpg",
    },
    {
      name: "twitter:image:alt",
      content: title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: currentUrl,
    },
  ];
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const start = formData.get("start") as string;
  const end = formData.get("end") as string;
  const initial_battery = formData.get("initial_battery") as string;
  const arrival_battery_target = formData.get(
    "arrival_battery_target",
  ) as string;
  const tab = formData.get("tab") as string;
  let range = formData.get("range") as string;
  let battery_capacity = formData.get("battery_capacity") as string;
  let car_id = formData.get("car_id") as string;
  const blocked_stations = formData.get("blocked_stations") as string | "";
  const rating = formData.get("rating") as string;
  const routeId = formData.get("routeId") as string;
  const intent = formData.get("intent") as string;

  if (intent === "rating") {
    await prisma.route.update({
      where: { id: routeId },
      data: { rating: parseInt(rating) },
    });

    return json({ success: true });
  }

  switch (tab) {
    case "manual":
      range = formData.get("range") as string;
      battery_capacity = formData.get("battery_capacity") as string;
      break;
    case "carro":
      const userCar = await prisma.userUserCar.findUnique({
        where: {
          id: parseInt(car_id),
        },
        include: {
          userCar: {
            include: {
              subtypes: true,
            },
          },
        },
      });

      if (!userCar?.range) {
        range = userCar?.userCar.range?.toString() ?? "";
      } else {
        range = userCar.range.toString();
      }

      if (!userCar?.batteryCapacity) {
        battery_capacity = userCar?.userCar.batteryCapacity?.toString() ?? "";
      } else {
        battery_capacity = userCar.batteryCapacity.toString();
      }

      if (range) {
        range = (parseFloat(range) / 1000).toString();
      }

      const subtypeName = userCar?.subtypeName;

      if (
        subtypeName !== "none" &&
        !userCar?.range &&
        !userCar?.batteryCapacity
      ) {
        const subtype = userCar?.userCar.subtypes.find(
          (subtype) => subtype.name === subtypeName,
        );
        const subtypeRange = subtype?.range;
        if (subtypeRange) {
          range = (subtypeRange / 1000).toString();
        }

        const subtypeBatteryCapacity = subtype?.batteryCapacity;
        if (subtypeBatteryCapacity) {
          battery_capacity = subtypeBatteryCapacity.toString();
        }
      }

      if (!battery_capacity) {
        battery_capacity = "40";
      }
      break;
    default:
      break;
  }

  const userId = await getUserId(request);

  const response = await fetch("https://evrouter.fly.dev/route", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      start,
      end,
      range,
      battery_capacity,
      initial_battery,
      arrival_battery_target,
      blocked_stations,
    }),
  });

  let data = await response.json();

  if (!response.ok) {
    const errorData = data as { detail?: string };
    await prisma.route.create({
      data: {
        start,
        end,
        range: parseFloat(range as string),
        batteryCapacity: parseFloat(battery_capacity),
        initialBattery: parseFloat(initial_battery),
        blockedStations: blocked_stations,
        carId: parseInt(car_id),
        arrivalBatteryTarget: parseFloat(arrival_battery_target),
        userId,
      },
    });
    return json(
      {
        error:
          errorData.detail || "Nenhuma rota encontrada. Escolha outros pontos.",
      },
      { status: response.status },
    );
  }

  const waypoints = JSON.stringify(data);

  if (start && end && range && battery_capacity && initial_battery) {
    let route = await prisma.route.create({
      data: {
        start,
        end,
        range: parseFloat(range as string),
        batteryCapacity: parseFloat(battery_capacity),
        initialBattery: parseFloat(initial_battery),
        waypoints,
        blockedStations: blocked_stations,
        carId: parseInt(car_id),
        arrivalBatteryTarget: parseFloat(arrival_battery_target),
        userId,
      },
    });

    const routeId = route?.id;

    if (routeId) {
      (data as any).routeId = routeId;
    }
  }

  return json(data);
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUserId(request);

  const ipCountry = request.headers.get("cf-ipcountry");
  const ipLongitude = request.headers.get("cf-iplongitude");
  const ipLatitude = request.headers.get("cf-iplatitude");

  const url = new URL(request.url);
  const searchParams = url.searchParams;

  let longitude = -51.965332;
  let latitude = -16.741428;
  let zoomLevel = 5;

  if (ipCountry === "BR" && ipLongitude && ipLatitude) {
    longitude = parseFloat(ipLongitude);
    latitude = parseFloat(ipLatitude);
    zoomLevel = 10;
  }

  if (searchParams.has("estacao")) {
    const stationId = searchParams.get("estacao");
    const station = await prisma.location.findUnique({
      where: { id: stationId ? parseInt(stationId) : undefined },
      select: {
        latitude: true,
        longitude: true,
      },
    });

    if (station) {
      latitude = station?.latitude ?? latitude;
      longitude = station?.longitude ?? longitude;
      zoomLevel = 14;
    }
  }

  if (!user) {
    return json({
      userCars: [],
      favorites: [],
      longitude,
      latitude,
      zoomLevel,
    });
  }

  const userCars = await prisma.userUserCar.findMany({
    where: {
      userId: user,
    },
    include: {
      userCar: true,
    },
  });

  const userFavorites = await prisma.favoriteStation.findMany({
    where: {
      userId: user,
    },
  });

  const favorites = userFavorites.map((fav) => fav.locationId);

  return json({ userCars, favorites, longitude, latitude, zoomLevel });
}

export default function MapaRoute() {
  const { userCars, favorites, longitude, latitude, zoomLevel } =
    useLoaderData<typeof loader>();

  return (
    <div>
      <Outlet />
      <ClientOnly
        fallback={
          <Skeleton className="h-[calc(100dvh-48px)] w-screen overflow-hidden" />
        }
      >
        {() => (
          <div className="h-[calc(100dvh-48px)] w-screen overflow-hidden">
            <Mapa
              chargingStations={[]}
              favorites={favorites}
              userLat={btoa(latitude.toString())}
              userLon={btoa(longitude.toString())}
              zoomLevel={zoomLevel}
              userCars={userCars}
            />
          </div>
        )}
      </ClientOnly>
    </div>
  );
}

export const handle: SEOHandle = {
  //@ts-ignore
  getSitemapEntries: async (request) => {
    return [
      {
        route: "/mapa",
        priority: 0.7,
        changefreq: "daily",
        lastmod: "2024-07-19T23:33:27.979Z",
      },
    ];
  },
};

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}